/**
 * Cambio de habitación.
 *
 * Un cambio de habitación NO es una edición de campos: mueve la reserva y el
 * estado de dos habitaciones a la vez. Por eso vive fuera de EditReservation y
 * se resuelve con un único endpoint (`api/reservation/change-room/:id`) que
 * hace todo del lado del servidor.
 */

import * as React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PaidIcon from '@mui/icons-material/Paid';
import { toast } from 'react-toastify';
import { getApi, patchApi } from 'views/services/api';
import { calculateReservationPrice } from 'utils/pricingUtils';

// La paleta del tema define warning.light casi blanco (#fff8e1), asi que un
// Alert severity="warning" queda lavado e invisible. Usamos el naranja de la
// paleta para que el aviso se lea de verdad.
const warningAlertSx = {
  color: 'orange.dark',
  backgroundColor: 'orange.light',
  border: '1px solid',
  borderColor: 'orange.main',
  '& .MuiAlert-icon': { color: 'orange.dark' }
};

const ChangeRoom = (props) => {
  const { open, handleClose, data } = props;

  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const currency = hotel?.currency || 'USD';

  const [rooms, setRooms] = useState([]);
  const [stayTypes, setStayTypes] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newRoomNo, setNewRoomNo] = useState('');
  const [reason, setReason] = useState('');

  const fetchRooms = async () => {
    setLoadingRooms(true);
    try {
      const [roomsRes, typesRes] = await Promise.all([
        getApi(`api/room/viewallrooms/${hotel?.hotelId}`),
        getApi(`api/hotel/stay-types/list/${hotel?.hotelId}`)
      ]);
      setRooms(roomsRes?.data || []);
      setStayTypes(typesRes?.data || []);
    } catch (error) {
      setRooms([]);
      toast.error('No se pudieron cargar las habitaciones');
    } finally {
      setLoadingRooms(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchRooms();
      setNewRoomNo('');
      setReason('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const currentRoom = useMemo(() => rooms.find((room) => String(room.roomNo) === String(data?.roomNo)), [rooms, data?.roomNo]);

  // Destino válido: no es la actual, está limpia, libre y en servicio.
  const availableRooms = useMemo(
    () =>
      rooms.filter(
        (room) =>
          String(room.roomNo) !== String(data?.roomNo) &&
          room.housekeepingStatus === 'clean' &&
          (!room.bookingStatus || room.bookingStatus === 'false')
      ),
    [rooms, data?.roomNo]
  );

  const selectedRoom = useMemo(() => rooms.find((room) => String(room.roomNo) === String(newRoomNo)), [rooms, newRoomNo]);

  // Misma funcion que usa la creacion y la edicion: lee la Matriz de Tarifas y
  // multiplica por noches solo si el tipo de estadia es nocturno.
  const rateOf = useCallback(
    (room) =>
      room
        ? Number(
            calculateReservationPrice({
              room,
              stayTypeId: data?.stayType,
              stayTypesList: stayTypes,
              checkInDate: data?.checkInDate,
              checkOutDate: data?.checkOutDate
            }).amount || 0
          )
        : 0,
    [data?.stayType, data?.checkInDate, data?.checkOutDate, stayTypes]
  );

  const oldRate = useMemo(() => rateOf(currentRoom), [currentRoom, rateOf]);
  const newRate = useMemo(() => rateOf(selectedRoom), [selectedRoom, rateOf]);
  const priceDifference = selectedRoom ? Number((newRate - oldRate).toFixed(2)) : 0;

  const guestIsCheckedIn = data?.status === 'active';

  const handleSubmit = async () => {
    if (!newRoomNo) {
      toast.error('Selecciona la habitación de destino');
      return;
    }

    setSubmitting(true);
    try {
      const response = await patchApi(`api/reservation/change-room/${data?._id}`, {
        newRoomNo,
        reason: reason.trim() || undefined
      });

      if (response.status === 200) {
        const { priceApplied, priceDifference: applied } = response.data || {};
        toast.success(
          priceApplied
            ? `Habitación cambiada a ${newRoomNo}. Se cargaron ${currency} ${applied} al saldo pendiente.`
            : `Habitación cambiada a ${newRoomNo}`
        );
        handleClose();
        window.dispatchEvent(new Event('reservationUpdated'));
      } else {
        toast.error(response?.data?.error || 'No se pudo cambiar la habitación');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo cambiar la habitación');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1.5, pt: 2 }}>
        <Typography variant="h6" fontWeight="700" fontSize="1.1rem">
          Cambiar de Habitación
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'error.main' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5, pb: 2 }}>
        {loadingRooms ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: 'background.neutral',
                  border: '1px dashed',
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Actual
                  </Typography>
                  <Chip label={`Hab. ${data?.roomNo ?? '—'}`} color="default" sx={{ fontWeight: 700, mt: 0.5 }} />
                  {currentRoom && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {currency} {oldRate}
                    </Typography>
                  )}
                </Box>

                <SwapHorizIcon color="primary" />

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Destino
                  </Typography>
                  <Chip
                    label={newRoomNo ? `Hab. ${newRoomNo}` : 'Sin elegir'}
                    color={newRoomNo ? 'primary' : 'default'}
                    sx={{ fontWeight: 700, mt: 0.5 }}
                  />
                  {selectedRoom && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {currency} {newRate}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <FormLabel sx={{ mb: 0.5, fontSize: '0.85rem', fontWeight: 500 }}>Nueva Habitación</FormLabel>
                <Select value={newRoomNo} onChange={(e) => setNewRoomNo(e.target.value)} displayEmpty sx={{ fontSize: '0.875rem' }}>
                  <MenuItem value="" disabled>
                    Seleccione una habitación disponible
                  </MenuItem>
                  {availableRooms.map((room) => (
                    <MenuItem key={room._id} value={room.roomNo}>
                      Hab. {room.roomNo} — {room.roomType || 'Estándar'} ({currency} {room.amount})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {availableRooms.length === 0 && (
                <Alert severity="warning" sx={{ mt: 1.5, ...warningAlertSx }}>
                  No hay habitaciones limpias y libres para mover esta reserva.
                </Alert>
              )}
            </Grid>

            {selectedRoom && priceDifference > 0 && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<PaidIcon fontSize="inherit" />}>
                  La habitación {newRoomNo} es más cara. Se cobrará automáticamente la diferencia de{' '}
                  <strong>
                    {currency} {priceDifference}
                  </strong>
                  , que se sumará al saldo pendiente de la reserva.
                </Alert>
              </Grid>
            )}

            {selectedRoom && priceDifference < 0 && (
              <Grid item xs={12}>
                <Alert severity="success">
                  La habitación {newRoomNo} es{' '}
                  <strong>
                    {currency} {Math.abs(priceDifference)}
                  </strong>{' '}
                  más barata. El saldo de la reserva no cambia.
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormLabel sx={{ mb: 0.5, fontSize: '0.85rem', fontWeight: 500, display: 'block' }}>Motivo (opcional)</FormLabel>
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                placeholder="Ej: aire acondicionado dañado, upgrade de cortesía..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity={guestIsCheckedIn ? 'warning' : 'info'} sx={guestIsCheckedIn ? warningAlertSx : undefined}>
                {guestIsCheckedIn
                  ? `El huésped ya hizo check-in: la habitación ${data?.roomNo} se marcará como sucia y la ${
                      newRoomNo || 'nueva'
                    } quedará ocupada.`
                  : `La reserva aún no tiene check-in: ambas habitaciones quedarán libres hasta la llegada del huésped.`}
              </Alert>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="outlined" size="medium" disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          size="medium"
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />}
          disabled={submitting || !newRoomNo}
        >
          Confirmar Cambio
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ChangeRoom.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    _id: PropTypes.string,
    roomNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stayType: PropTypes.string,
    status: PropTypes.string,
    checkInDate: PropTypes.string,
    checkOutDate: PropTypes.string,
    totalAmount: PropTypes.number
  }).isRequired
};

export default ChangeRoom;
